import { z } from 'zod/v4';

/**
 * One row of an action's or plan's field-change log.
 *
 * Actions and plans share `corrective_action_plan_service_actions`, and the
 * history table keys off `action_id`, so **`actionId` carries the plan id on the
 * plan route**. The public projection renames it (`entityId` + `entityType`);
 * the internal wire keeps the column name it has always had.
 *
 * Three fields are resolved after the read rather than joined in SQL, and each
 * has a caveat worth knowing before you render it:
 *
 * - **`firstName` / `lastName` are censored** to a first character plus
 *   asterisks (`Bang` → `B***`) when the actor's company is neither the reading
 *   company nor one it holds a `companies_service_company_links` row to. A
 *   one-character name is returned as-is and an empty one as `null`, so a
 *   censored value is not distinguishable from a short real one — never parse
 *   these back.
 * - **`companyName` is the reading company's own view** of the actor's company
 *   where one exists — `official_name` from the profile that company wrote
 *   *about* the actor's company — and otherwise the actor's company's own
 *   `display_name` (its self profile; CAPA batch E). It is `null` only when the
 *   company has neither. Other CAPA reads use the self-profile `display_name`
 *   outright; the remaining divergence is recorded in the migration doc's
 *   backlog.
 * - **`prevState` / `newState` are heterogeneous** and deliberately typed
 *   `unknown`. What they hold depends on `property` (verified against every
 *   modern writer, 2026-09-12):
 *
 *   | `property` | state shape |
 *   | --- | --- |
 *   | `assignees`, `reporters` | `{ userId, companyId, firstName, lastName, companyName }` — the last three added by the same enrichment as above |
 *   | `collaborators` | `{ companyId, companyName }` |
 *   | `linkedActions` | `{ actionId }` — one object per row, not an array |
 *   | `attachments` | `{ fileId }` — likewise one object |
 *   | `comment` | `{ commentId, visibility }` (`added` only) |
 *   | `remediationRequest` | `{ message, attachmentIds, status }` on `added`; `{ message }` on `changed` |
 *   | `status`, `priority`, `title`, `description`, `impactAreaId`, `sourceId`, `sourceType`, `type`, `isArchived` | a bare scalar |
 *   | `dueDate`, `startedAt`, `endedAt` | an ISO date string |
 *   | `null` with `type: 'created'` | `newState` is `{ id }` (or `null` on seeded rows) |
 *   | `null` with `type: 'deleted'` | `prevState` is the **whole action row** as `bulkDelete` read it |
 *
 *   Rows the retired CAPA v1 service wrote (`users.assignee`, `action:status`,
 *   `{ id }` values…) were rewritten into this vocabulary when the log moved
 *   to `entity_history` (`202611021600-backfill-capa-activity-history`), so
 *   every reader sees one vocabulary.
 *
 *   `isArchived` rows include the **reading company's own** archive and
 *   restore (`0` → `1`, `1` → `0`): archiving is per company, so another
 *   company's never appear.
 *
 *   Narrowing this to a union would strip the keys the dashboards read, so the
 *   shape is documented here instead of encoded.
 *
 * `type` is one of `CAPA_HISTORY_TYPES` — see the note there on why it is not an
 * enum.
 */
export const HistoryEntrySchema = z.object({
  id: z.string(),
  /** The action or plan this row belongs to (`action_id`). */
  actionId: z.string(),
  userId: z.string(),
  companyId: z.string(),
  companyName: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  type: z.string(),
  /** `null` on a `created` row — nothing changed, the entity appeared. */
  property: z.string().nullable(),
  prevState: z.unknown().nullable(),
  newState: z.unknown().nullable(),
  createdAt: z.date(),
});

export type HistoryEntry = z.infer<typeof HistoryEntrySchema>;
